import { useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import find from "lodash/find";
import { useAuth } from "../contexts/authContext";
import { queryKeys } from "./keys";
import {
    getCustomers, getDashboard, getDashboardMonth, getEmptyReport, getMe, getProjects,
    getProjectsWithDetails, getProjectWithDetails, getReports, getTask, getTasks, getTools, getUsers
} from "../services/dataService";
import { getSettings } from "../services/settingsService";
import ICustomer from "../interfaces/ICustomer";
import IDashboard from "../interfaces/IDashboard";
import IMe from "../interfaces/IMe";
import IProject from "../interfaces/IProject";
import IReport from "../interfaces/IReport";
import ITask from "../interfaces/ITask";
import ITool from "../interfaces/ITool";
import IUser from "../interfaces/IUser";

const MINUTE_IN_MS = 60 * 1000;

type QueryOptions<TData> = Omit<UseQueryOptions<TData, unknown, TData, readonly unknown[]>, "queryKey" | "queryFn">;

// The access token is refreshed every five minutes, keeping it out of the key prevents a refetch of everything
const useAuthorizedQuery = <TData>(
    queryKey: readonly unknown[],
    queryFn: (accessToken: string) => Promise<TData>,
    options?: QueryOptions<TData>
): UseQueryResult<TData, unknown> => {
    const { state: authState } = useAuth();
    const { accessToken: token } = authState;

    return useQuery<TData, unknown, TData, readonly unknown[]>({
        queryKey,
        queryFn: () => queryFn(token ?? ""),
        ...options,
        enabled: !!token && (options?.enabled ?? true)
    });
};

export const useMe = (): UseQueryResult<IMe, unknown> =>
    useAuthorizedQuery<IMe>(queryKeys.me, getMe, { staleTime: 60 * MINUTE_IN_MS });

export const useReports = (): UseQueryResult<IReport[], unknown> => {
    const { weeksToLoad } = getSettings();
    return useAuthorizedQuery<IReport[]>(
        queryKeys.reportsForWeeks(weeksToLoad),
        (token: string) => getReports(token, weeksToLoad)
    );
};

// The report detail and the report form work with a single item out of the loaded list
export const useReport = (id?: string) => {
    const { data, isFetching, error } = useReports();
    // Has to keep its identity between renders, the report form initializes itself from it
    const report = useMemo(
        () => (id ? find(data, (x: IReport) => x.id === id) : getEmptyReport()),
        [data, id]
    );

    return { report, isFetching, error };
};

export const useProjects = (): UseQueryResult<IProject[], unknown> =>
    useAuthorizedQuery<IProject[]>(queryKeys.projects, getProjects);

export const useProjectsWithDetails = (viewId: number): UseQueryResult<IProject[], unknown> =>
    useAuthorizedQuery<IProject[]>(
        queryKeys.projectsWithDetailsForView(viewId),
        (token: string) => getProjectsWithDetails(token, viewId)
    );

export const useProject = (id?: string): UseQueryResult<IProject, unknown> => {
    const queryClient = useQueryClient();

    // The list of projects already carries the full detail, use it until the fresh one arrives
    const findInLoadedLists = (): IProject | undefined => {
        const lists = queryClient.getQueriesData<IProject[]>(queryKeys.projectsWithDetails);
        for (const [, projects] of lists) {
            const found = find(projects, (x: IProject) => x.id === id);
            if (found) { return found; }
        }

        return undefined;
    };

    return useAuthorizedQuery<IProject>(
        queryKeys.project(id ?? ""),
        (token: string) => getProjectWithDetails(token, id ?? ""),
        { enabled: !!id, initialData: findInLoadedLists }
    );
};

export const useCustomers = (): UseQueryResult<ICustomer[], unknown> =>
    useAuthorizedQuery<ICustomer[]>(queryKeys.customers, getCustomers, { staleTime: 10 * MINUTE_IN_MS });

export const useTasks = (viewId: number): UseQueryResult<ITask[], unknown> =>
    useAuthorizedQuery<ITask[]>(queryKeys.tasksForView(viewId), (token: string) => getTasks(token, viewId));

export const useTask = (id?: string): UseQueryResult<ITask, unknown> => {
    const queryClient = useQueryClient();

    // The loaded list already carries everything the form needs, use it until the fresh one arrives
    const findInLoadedLists = (): ITask | undefined => {
        const lists = queryClient.getQueriesData<ITask[]>(queryKeys.tasks);
        for (const [, tasks] of lists) {
            const found = find(tasks, (x: ITask) => x.id === id);
            if (found) { return found; }
        }

        return undefined;
    };

    return useAuthorizedQuery<ITask>(
        queryKeys.task(id ?? ""),
        (token: string) => getTask(token, id ?? ""),
        { enabled: !!id, initialData: findInLoadedLists }
    );
};

export const useUsers = (): UseQueryResult<IUser[], unknown> =>
    useAuthorizedQuery<IUser[]>(queryKeys.users, getUsers, { staleTime: 10 * MINUTE_IN_MS });

export const useTools = (): UseQueryResult<ITool[], unknown> =>
    useAuthorizedQuery<ITool[]>(queryKeys.tools, getTools, { staleTime: 10 * MINUTE_IN_MS });

export const useDashboard = (startOfWeek: Date): UseQueryResult<IDashboard, unknown> =>
    useAuthorizedQuery<IDashboard>(
        queryKeys.dashboardForWeek(startOfWeek.toISOString()),
        (token: string) => getDashboard(token, startOfWeek)
    );

export const useDashboardMonth = (startOfMonth: Date): UseQueryResult<number, unknown> =>
    useAuthorizedQuery<number>(
        queryKeys.dashboardMonthForMonth(startOfMonth.toISOString()),
        (token: string) => getDashboardMonth(token, startOfMonth)
    );

// Replaces the reportsChanged / projectsChanged / tasksChanged flags. Marking a query as invalid
// makes it refetch as soon as it is rendered again, no matter whether the user waited for the
// previous load to finish.
export const useInvalidateData = () => {
    const queryClient = useQueryClient();

    const invalidateReports = () => {
        queryClient.invalidateQueries(queryKeys.reports);
        queryClient.invalidateQueries(queryKeys.dashboard);
        queryClient.invalidateQueries(queryKeys.dashboardMonth);
    };

    const invalidateProjects = () => {
        queryClient.invalidateQueries(queryKeys.projects);
        queryClient.invalidateQueries(queryKeys.projectsWithDetails);
        queryClient.invalidateQueries(queryKeys.projectDetail);
    };

    const invalidateTasks = () => {
        queryClient.invalidateQueries(queryKeys.tasks);
        queryClient.invalidateQueries(queryKeys.taskDetail);
    };

    return { invalidateReports, invalidateProjects, invalidateTasks };
};
